import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { AuthContext } from "../../../contexts/AuthContext";
import { getTicketMessages, sendTicketMessage } from "../../../utilities/api/adminService";
import Breadcrumb from "../../breadcrumb/Breadcrumb";
import Loader from "../../loading/Loader";
import StatusBadge from "../../table/StatusBadge";
import { Send2, AttachSquare, CloseCircle } from "iconsax-react";
import FileAttachment from "../../shared/FileAttachment";

const TicketChat: React.FC = () => {
  const { accessToken } = useContext(AuthContext);
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingLoader, setSendingLoader] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { ticketId } = useParams();

  const ticketIdNumber = Number(ticketId);
  const chatContainerRef = useRef<HTMLUListElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchChat = async () => {
      setLoading(true);
      setError(null);
      if (!accessToken) {
        setError("Authentication failed. Please log in again.");
        enqueueSnackbar("Authentication failed. Please log in again.", { variant: "error" });
        setLoading(false);
        return;
      }
      try {
        const response = await getTicketMessages(accessToken, ticketIdNumber.toString());
        if (response.ok) {
          setChat(response.data);
          setError(null);
          scrollToBottom();
        } else {
          const errorMsg = response.message || "Unable to load chat. Please try again.";
          setError(errorMsg);
          enqueueSnackbar(errorMsg, { variant: "error" });
        }
      } catch (err: any) {
        console.error("Error fetching chat:", err);
        const errorMsg = "An error occurred while loading the chat. Please try again.";
        setError(errorMsg);
        enqueueSnackbar(errorMsg, { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchChat();
  }, [accessToken, ticketIdNumber]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      enqueueSnackbar(`File size must be less than 10MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`, { variant: "error" });
      return;
    }

    // Validate file type
    const allowedExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt"];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      enqueueSnackbar(`File type not allowed. Allowed types: ${allowedExtensions.join(", ")}`, { variant: "error" });
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String.split(",")[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSendMessage = async () => {
    if (!message.trim() && !selectedFile) {
      enqueueSnackbar("Please enter a message or attach a file", { variant: "error" });
      return;
    }
    setSendingLoader(true);
    setIsSubmitting(true);
    try {
      let fileBase64: string | undefined;
      if (selectedFile) {
        fileBase64 = await fileToBase64(selectedFile);
      }

      const sendResponse = await sendTicketMessage(accessToken!, {
        ticket_id: ticketIdNumber.toString(),
        message: message.trim() || "Attachment",
        file: fileBase64,
      });
      if (sendResponse.ok) {
        setMessage("");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
        }
        enqueueSnackbar("Message sent successfully", { variant: "success" });
        const chatResponse = await getTicketMessages(accessToken!, ticketIdNumber.toString());
        if (chatResponse.ok) {
          setChat(chatResponse.data);
          scrollToBottom();
        } else {
          enqueueSnackbar(chatResponse.message || "Unable to refresh chat. Please try again.", { variant: "error" });
        }
      } else {
        enqueueSnackbar(sendResponse.message || "Unable to send message. Please try again.", { variant: "error" });
      }
    } catch (err: any) {
      console.error("Error sending message or refreshing chat:", err);
      enqueueSnackbar("An unexpected error occurred. Please try again.", { variant: "error" });
    } finally {
      setSendingLoader(false);
      setIsSubmitting(false);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  return (
    <div className="flex flex-col w-full overflow-hidden border divide-y divide-gorgonzola-blue-50 rounded-xl border-gorgonzola-blue-50">
      <Breadcrumb isId />

      {loading ? (
        <div className="flex justify-center w-full p-4 md:p-6">
          <Loader />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center w-full p-6 md:p-8">
          <div className="flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-english-vermillion-100">
            <span className="text-2xl">⚠️</span>
          </div>
          <p className="mb-1 text-base font-medium text-closed-shutter-300">Failed to load ticket</p>
          <p className="max-w-sm mb-6 text-sm text-center text-closed-shutter-200">{error}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 text-sm font-medium text-white transition-colors rounded-lg bg-gorgonzola-blue-300 hover:bg-gorgonzola-blue-400">
            Try Again
          </button>
        </div>
      ) : (
        <div className="w-full p-4 md:p-6">
          <div className="flex flex-col border divide-y rounded-md divide-gorgonzola-blue-50 border-gorgonzola-blue-50">
            <div className="flex items-center justify-between px-4 py-6">
              <h2 className="text-base font-bold capitalize text-closed-shutter-300">{chat?.ticket.title}</h2>
              {chat?.ticket.status ? <StatusBadge statusText={chat?.ticket.status} /> : null}
            </div>

            <div className="w-full p-4 pt-0">
              <ul ref={chatContainerRef} className="flex flex-col mb-4 list-none gap-14 max-h-[30rem] overflow-y-auto hide_scroll">
                {!chat?.messages || chat.messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center min-h-[15rem] text-center">
                    <p className="text-sm text-closed-shutter-200">No messages yet.</p>
                    <p className="mt-1 text-xs text-closed-shutter-200/70">This ticket has no messages. Start responding to help the customer.</p>
                  </div>
                ) : (
                  chat.messages.map((message: any, i: number) => (
                    <li key={i} className={`w-full flex items-center py-2 mb-2 ${message.message.role === "client" || message.message.role === "user" ? "" : "justify-end"}`}>
                      <div className={`flex flex-col items-end w-full gap-2`}>
                        <div className={`flex w-full ${message.message.role === "client" || message.message.role === "user" ? "justify-end flex-row-reverse pe-2" : " justify-end ps-2"}`}>
                          <div
                            className={`w-fit max-w-full px-3 py-2 ${
                              message.message.role === "client" || message.message.role === "user" ? "bg-[#F9F9FA] rounded-e-md rounded-bl-md" : "bg-gorgonzola-blue-300 rounded-s-md rounded-br-md"
                            }`}
                          >
                            <p className={`whitespace-pre-wrap break-words ${message.message.role === "client" || message.message.role === "user" ? "text-closed-shutter-300" : "text-white"}`}>
                              {message.message.message}
                            </p>
                            {message.attachment && (
                              <FileAttachment fileName={message.attachment.file_name} mimeType={message.attachment.mime_type} fileUrl={`${message.host}/${message.attachment.file_name}`} />
                            )}
                          </div>

                          <div className="*:min-w-2 w-2">
                            {message.message.role === "client" || message.message.role === "user" ? (
                              <img src="/assets/image/get-help/top-vector-tip2.svg" alt="vector" />
                            ) : (
                              <img src="/assets/image/get-help/top-vector-tip.svg" alt="vector" />
                            )}
                          </div>
                        </div>

                        <div className={`w-full inline-flex ${message.message.role === "client" || message.message.role === "user" ? " justify-start " : "  justify-end "}`}>
                          <span className="text-xs text-gorgonzola-blue-200">{moment(message.message.created_at * 1000).format("YYYY/DD/MM, h:mm:ss a")}</span>
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className="flex flex-col w-full gap-3 px-4 py-3"
              >
                {selectedFile && (
                  <div className="flex items-center gap-2 p-3 border rounded-lg bg-gorgonzola-blue-50 border-gorgonzola-blue-100">
                    <AttachSquare className="flex-shrink-0 size-5 text-gorgonzola-blue-300" variant="Bold" />
                    <span className="flex-grow text-sm truncate text-closed-shutter-300">{selectedFile.name}</span>
                    <span className="flex-shrink-0 text-xs text-closed-shutter-200">{(selectedFile.size / 1024).toFixed(1)} KB</span>
                    <button type="button" onClick={handleRemoveFile} className="flex-shrink-0 p-1 transition-colors rounded hover:bg-english-vermillion-50">
                      <CloseCircle className="size-5 text-english-vermillion-300" variant="Bold" />
                    </button>
                  </div>
                )}

                <div
                  className={`flex items-center gap-3 w-full text-sm transition-all duration-200 border rounded-lg text-closed-shutter-300 ${
                    sendingLoader
                      ? "bg-closed-shutter-100 border-closed-shutter-100 cursor-not-allowed"
                      : "bg-[#F9F9FA] border-gorgonzola-blue-50 hover:border-gorgonzola-blue-100 focus-within:border-gorgonzola-blue-200"
                  }`}
                >
                  <div className="flex-grow px-4 py-3">
                    {sendingLoader ? (
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-closed-shutter-300/70">Sending...</span>
                      </div>
                    ) : (
                      <>
                        <textarea
                          ref={textareaRef}
                          value={message}
                          maxLength={5000}
                          onChange={(e) => setMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          placeholder="Type your message here..."
                          rows={1}
                          disabled={isSubmitting}
                          className="w-full leading-relaxed bg-transparent outline-none resize-none text-closed-shutter-300 placeholder:text-closed-shutter-200"
                          style={{
                            minHeight: "1.5rem",
                            maxHeight: "10rem",
                            overflowY: "auto",
                          }}
                          onInput={(e) => {
                            e.currentTarget.style.height = "auto";
                            const newHeight = Math.min(e.currentTarget.scrollHeight, 160);
                            e.currentTarget.style.height = newHeight + "px";
                          }}
                        />
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-xs text-closed-shutter-200">{message.length}/5000</span>
                          {message.length > 4500 && <span className="text-xs text-english-vermillion-300">{5000 - message.length} characters left</span>}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2 px-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      disabled={sendingLoader || isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sendingLoader || isSubmitting}
                      className={`flex-shrink-0 p-2.5 rounded-lg transition-all duration-200 ${
                        sendingLoader || isSubmitting
                          ? "bg-closed-shutter-100 text-closed-shutter-200 cursor-not-allowed"
                          : "bg-gorgonzola-blue-50 text-gorgonzola-blue-300 hover:bg-gorgonzola-blue-100 active:scale-95"
                      }`}
                      title="Attach file"
                    >
                      <AttachSquare className="size-5" variant="Bold" />
                    </button>

                    <button
                      type="submit"
                      disabled={isSubmitting || (!message.trim() && !selectedFile) || sendingLoader}
                      className={`flex-shrink-0 p-2.5 rounded-lg transition-all duration-200 ${
                        isSubmitting || (!message.trim() && !selectedFile) || sendingLoader
                          ? "bg-gorgonzola-blue-100 text-closed-shutter-200 cursor-not-allowed"
                          : "bg-gorgonzola-blue-300 text-white hover:bg-gorgonzola-blue-400 hover:shadow-md active:scale-95"
                      }`}
                      title={!message.trim() && !selectedFile ? "Type a message or attach a file" : "Send message (Enter)"}
                    >
                      <Send2 className="size-5" variant="Bold" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketChat;
